import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const CLIENT_ID_KEY = 'bilbase_client_id';

export function getClientId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
}

export interface VariantVoteData {
  likes: number;
  dislikes: number;
  total: number;
  myVote: 1 | -1 | null;
}

export function useVariantVote(variantId: string | undefined) {
  const queryClient = useQueryClient();
  const clientId = getClientId();

  const query = useQuery({
    queryKey: ['variant-vote', variantId, clientId],
    enabled: !!variantId,
    staleTime: 30_000,
    queryFn: async (): Promise<VariantVoteData> => {
      if (!variantId) return { likes: 0, dislikes: 0, total: 0, myVote: null };

      const [statsRes, mineRes] = await Promise.all([
        (supabase as any).rpc('get_variant_vote_stats', {
          _variant_ids: [variantId],
        }),
        (supabase as any).rpc('get_my_variant_vote', {
          _variant_id: variantId,
          _client_id: clientId,
        }),
      ]);

      const stats = ((statsRes.data as any[]) || [])[0] || { likes: 0, dislikes: 0, total: 0 };
      const myVote = (mineRes.data as 1 | -1 | null) ?? null;

      return {
        likes: stats.likes ?? 0,
        dislikes: stats.dislikes ?? 0,
        total: stats.total ?? 0,
        myVote,
      };

    },
  });

  const mutation = useMutation({
    mutationFn: async (vote: 1 | -1 | null) => {
      if (!variantId) return;

      if (vote === null) {
        const { error } = await (supabase as any).rpc('clear_variant_vote', {
          _variant_id: variantId,
          _client_id: clientId,
        });
        if (error) throw error;
        return;
      }

      const { error } = await (supabase as any).rpc('cast_variant_vote', {
        _variant_id: variantId,
        _client_id: clientId,
        _vote: vote,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-vote', variantId, clientId] });
      queryClient.invalidateQueries({ queryKey: ['variant-vote-stats-all'] });
    },
  });

  return {
    ...query.data,
    isLoading: query.isLoading,
    setVote: mutation.mutate,
    isPending: mutation.isPending,
  };
}
